import {
  Camera,
  Clock,
  Edit3,
  Globe,
  Mail,
  MapPin,
  Phone,
  Save,
  Star,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { useAppSelector } from "@/hooks/redux";
import { authService } from "@/services/authService";
import businessInfoService from "@/services/businessInfoService";

interface BusinessProfile {
  address: string;
  businessLicense?: string;
  businessName: string;
  businessType: string;
  description: string;
  email: string;
  operatingHours: {
    [key: string]: { close: string; isOpen: boolean; open: string };
  };
  phone: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  taxId?: string;
  website?: string;
}

const BusinessProfileComponent: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  // Replace with actual business id logic (e.g. from user or route)
  const businessId = 1;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const info = await businessInfoService.getBusinessInfo();
        setProfile({
          address: info.address || "",

          businessName: info.company_name || "",
          businessType: info.business_type || "",
          description: info.description || "",
          email: info.email || "",
          operatingHours: info.operating_hours || {},
          phone: info.phone || "",
          website: info.website || "",
        });
      } catch (err: any) {
        setError("Failed to load business profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const getBusinessTypeLabel = (type: string) => {
    switch (type) {
      case "CourierOwner":
        return "Courier Service";
      case "FleetOwner":
        return "Fleet Management";
      case "RestaurantOwner":
        return "Restaurant";
      default:
        return "Business";
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    try {
      setLoading(true);
      setError(null);
      await authService.updateBusinessProfile(businessId, {
        address: profile.address,
        description: profile.description,
        email: profile.email,
        name: profile.businessName,
        operating_hours: profile.operatingHours,
        phone: profile.phone,
        type: profile.businessType,
        website: profile.website,
        // Add other fields as needed
      });
      setIsEditing(false);
    } catch (err: any) {
      setError("Failed to update business profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setProfile((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleHoursChange = (
    day: string,
    field: "close" | "isOpen" | "open",
    value: boolean | string,
  ) => {
    setProfile((prev) =>
      prev
        ? {
            ...prev,
            operatingHours: {
              ...prev.operatingHours,
              [day]: {
                ...prev.operatingHours[day],
                [field]: value,
              },
            },
          }
        : prev,
    );
  };

  const dayNames = {
    friday: "Friday",
    monday: "Monday",
    saturday: "Saturday",
    sunday: "Sunday",
    thursday: "Thursday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
  };

  if (loading)
    return (
      <div className="min-h-screen px-2 py-4 dark:bg-gray-900 sm:px-4 md:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-4xl">
          {/* Header Shimmer */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <div className="mb-2 h-8 w-48 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-5 w-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="h-10 w-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
          </div>

          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-3">
            {/* Main Content Shimmer */}
            <div className="order-2 lg:order-1 lg:col-span-2">
              <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
                {/* Profile Header Shimmer */}
                <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row">
                  <div className="h-16 w-16 animate-pulse rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 sm:h-20 sm:w-20"></div>
                  <div className="flex-1 space-y-2 text-center sm:text-left">
                    <div className="mx-auto h-7 w-48 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:mx-0"></div>
                    <div className="mx-auto h-5 w-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:mx-0"></div>
                  </div>
                </div>

                {/* Fields Shimmer */}
                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                  {[...Array(6)].map((_, i) => (
                    <div className={i >= 4 ? "md:col-span-2" : ""} key={i}>
                      <div className="mb-2 h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div
                        className={`h-${i >= 4 ? "20" : "10"} w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700`}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar Shimmer */}
            <div className="order-1 space-y-6 lg:order-2">
              {/* Stats Card Shimmer */}
              <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
                <div className="mb-4 h-6 w-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div className="flex items-center justify-between" key={i}>
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                        <div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      </div>
                      <div className="h-5 w-12 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Operating Hours Shimmer */}
              <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
                <div className="mb-4 h-6 w-36 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                <div className="space-y-3">
                  {[...Array(7)].map((_, i) => (
                    <div className="flex items-center justify-between" key={i}>
                      <div className="h-4 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-4 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!profile) return <div className="p-6">No profile data.</div>;

  return (
    <div className="min-h-screen px-2 py-4 dark:bg-gray-900 sm:px-4 md:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl">
              Business Profile
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Manage your business information and settings.
            </p>
          </div>
          <button
            className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white opacity-50 sm:w-auto"
            disabled
            onClick={() => {}}
          >
            {isEditing ? (
              <Save className="h-4 w-4" />
            ) : (
              <Edit3 className="h-4 w-4" />
            )}
            {isEditing ? "Save Changes" : "Edit Profile"}
          </button>
        </div>

        <div className="flex flex-col gap-8 lg:grid lg:grid-cols-3">
          {/* Business Info Card */}
          <div className="order-2 lg:order-1 lg:col-span-2">
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row">
                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold text-white sm:h-20 sm:w-20 sm:text-2xl">
                    {profile.businessName.charAt(0)}
                  </div>
                  {isEditing && (
                    <button className="absolute -bottom-1 -right-1 rounded-full bg-blue-600 p-1.5 text-white hover:bg-blue-700">
                      <Camera className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
                    {profile.businessName}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                    {getBusinessTypeLabel(profile.businessType)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                {/* Business Name */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Name
                  </label>
                  {isEditing ? (
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("businessName", e.target.value)
                      }
                      type="text"
                      value={profile.businessName}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">
                      {profile.businessName}
                    </p>
                  )}
                </div>

                {/* Business Type */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Type
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {getBusinessTypeLabel(profile.businessType)}
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      type="tel"
                      value={profile.phone}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <p className="text-gray-900 dark:text-gray-100">
                        {profile.phone}
                      </p>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      type="email"
                      value={profile.email}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <p className="text-gray-900 dark:text-gray-100">
                        {profile.email}
                      </p>
                    </div>
                  )}
                </div>

                {/* Website */}
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Website
                  </label>
                  {isEditing ? (
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("website", e.target.value)
                      }
                      placeholder="https://www.yourbusiness.com"
                      type="url"
                      value={profile.website || ""}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <a
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        href={`https://${profile.website}`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {profile.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Address
                  </label>
                  {isEditing ? (
                    <textarea
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      rows={3}
                      value={profile.address}
                    />
                  ) : (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 text-gray-400" />
                      <p className="text-gray-900 dark:text-gray-100">
                        {profile.address}
                      </p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Description
                  </label>
                  {isEditing ? (
                    <textarea
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      placeholder="Describe your business..."
                      rows={4}
                      value={profile.description}
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-gray-100">
                      {profile.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="order-1 space-y-6 lg:order-2">
            {/* Business Stats */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                Business Stats
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Rating
                    </span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    4.7/5
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Customers
                    </span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    1,234
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Since
                    </span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    2022
                  </span>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                Operating Hours
              </h3>
              <div className="space-y-3">
                {Object.entries(profile.operatingHours).map(([day, hours]) => (
                  <div
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0"
                    key={day}
                  >
                    <span className="text-sm font-medium capitalize text-gray-700 dark:text-gray-300">
                      {dayNames[day as keyof typeof dayNames]}
                    </span>
                    {isEditing ? (
                      <div className="flex flex-col items-center gap-2 sm:flex-row">
                        <input
                          checked={hours.isOpen}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onChange={(e) =>
                            handleHoursChange(day, "isOpen", e.target.checked)
                          }
                          type="checkbox"
                        />
                        {hours.isOpen && (
                          <div className="flex gap-2">
                            <input
                              className="w-20 rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              onChange={(e) =>
                                handleHoursChange(day, "open", e.target.value)
                              }
                              type="time"
                              value={hours.open}
                            />
                            <span className="text-xs text-gray-400">-</span>
                            <input
                              className="w-20 rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              onChange={(e) =>
                                handleHoursChange(day, "close", e.target.value)
                              }
                              type="time"
                              value={hours.close}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {hours.isOpen
                          ? `${hours.open} - ${hours.close}`
                          : "Closed"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfileComponent;
